import { supabase, SUPABASE_ANON_KEY, SUPABASE_URL } from './supabaseClient';

export interface InternalTenantOverviewRow {
  companyId: string;
  companyName: string;
  plan: string;
  status: 'active' | 'trial' | 'expired' | 'cancelled';
  activeAdmins: number;
  lastActivityAt: string | null;
  aiQueriesUsed: number;
  aiOverageAmount: number;
  aiFallbackRate30d: number;
  emailsSentLast30d: number;
  emailLastSentAt: string | null;
  emailIssueRate30d: number;
}

export interface InternalTenantsOverviewKpis {
  activeTenants: number;
  trialTenants: number;
  paidTenants: number;
  aiFallbackRate30d: number;
  aiFallbackCount30d: number;
  aiTotalQueries30d: number;
  emailIssueRate30d: number;
  emailIssueCount30d: number;
  emailTotal30d: number;
}

export interface InternalTenantsOverviewResponse {
  rows: InternalTenantOverviewRow[];
  kpis: InternalTenantsOverviewKpis;
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface InternalSupportActionRow {
  request_id: string;
  created_at: string;
  actor_user_id: string;
  target_company_id: string;
  target_company_name: string;
  previous_plan: string | null;
  new_plan: string;
  reason: string;
}

async function getAccessToken(): Promise<string> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) {
    throw new Error('Authentication required.');
  }
  return accessToken;
}

async function callInternalFunction<T>(functionName: string, body: Record<string, unknown>): Promise<T> {
  const accessToken = await getAccessToken();
  const response = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = typeof payload?.error === 'string'
      ? payload.error
      : `Edge Function returned HTTP ${response.status}`;
    throw new Error(message);
  }

  return payload as T;
}

export interface InternalTenantDetail {
  company: {
    id: string;
    name: string;
    plan: string;
    subscriptionStatus: string | null;
    billingEmail: string | null;
    trialEndsAt: string | null;
    aiSpendingCap: number;
    planLimits: Record<string, unknown> | null;
    advisorEnabled: boolean;
    billingSyncLastAt: string | null;
    billingSyncLastStatus: string | null;
    billingSyncLastMessage: string | null;
  };
  overview: {
    toolCount: number;
    employeeCount: number;
    adminCount: number;
    lastActivityAt: string | null;
  };
  aiUsage: {
    periodStart: string | null;
    periodEnd: string | null;
    toolsCount: number;
    aiQueriesUsed: number;
    aiQueriesOverage: number;
    aiOverageAmount: number;
  } | null;
  emails: {
    sentLast30d: number;
    lastSentAt: string | null;
    templates: Array<{ template: string; count: number }>;
  };
  users: Array<{
    id: string;
    name: string;
    email: string;
    role: string | null;
    authRole: string | null;
    invitedAt: string | null;
    lastLoginAt: string | null;
  }>;
  audit: Array<{
    id: string;
    createdAt: string;
    eventType: string;
    message: string;
    toolName: string | null;
    requesterName: string | null;
    newStatus: string | null;
  }>;
}

export async function getInternalTenantsOverview(params: {
  plan?: string;
  status?: string;
  recentActivity?: '7d' | '30d' | 'stale' | 'all';
  aiHealth?: 'all' | 'fallback';
  emailHealth?: 'all' | 'issue';
  page?: number;
  pageSize?: number;
} = {}): Promise<InternalTenantsOverviewResponse> {
  const payload = await callInternalFunction<{ data?: InternalTenantsOverviewResponse; error?: string }>(
    'internal-tenants-overview',
    {
      plan: params.plan ?? 'all',
      status: params.status ?? 'all',
      recentActivity: params.recentActivity ?? 'all',
      aiHealth: params.aiHealth ?? 'all',
      emailHealth: params.emailHealth ?? 'all',
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 10,
    }
  );
  if (!payload?.data) {
    throw new Error(payload?.error || 'Unable to load tenants overview.');
  }

  return payload.data;
}

export async function getInternalTenantDetail(companyId: string): Promise<InternalTenantDetail> {
  const normalizedCompanyId = String(companyId || '').trim();
  if (!normalizedCompanyId) {
    throw new Error('Company ID is required.');
  }

  const payload = await callInternalFunction<{ data?: InternalTenantDetail; error?: string }>(
    'internal-tenant-detail',
    { companyId: normalizedCompanyId }
  );
  if (!payload?.data) {
    throw new Error(payload?.error || 'Unable to load tenant detail.');
  }

  return payload.data;
}

export async function toggleInternalTenantAdvisor(params: {
  companyId: string;
  advisorEnabled: boolean;
  reason: string;
}): Promise<{ companyId: string; advisorEnabled: boolean; previousValue: boolean }> {
  const payload = await callInternalFunction<{
    data?: { companyId: string; advisorEnabled: boolean; previousValue: boolean };
    error?: string;
  }>(
    'internal-tenant-toggle-advisor',
    {
      companyId: params.companyId,
      advisorEnabled: params.advisorEnabled,
      reason: params.reason,
    }
  );
  if (!payload?.data) {
    throw new Error(payload?.error || 'Unable to update advisor flag.');
  }

  return payload.data;
}

export async function forceInternalTenantBillingSync(params: {
  companyId: string;
  reason: string;
}): Promise<{ companyId: string; syncedAt: string; status: string; message: string }> {
  const payload = await callInternalFunction<{
    data?: { companyId: string; syncedAt: string; status: string; message: string };
    error?: string;
  }>(
    'internal-tenant-force-billing-sync',
    {
      companyId: params.companyId,
      reason: params.reason,
    }
  );
  if (!payload?.data) {
    throw new Error(payload?.error || 'Unable to run billing sync.');
  }

  return payload.data;
}

export async function overrideInternalTenantPlan(params: {
  companyId: string;
  targetPlan: 'tester' | 'pro' | 'trial';
  reason: string;
}): Promise<{
  companyId: string;
  requestId: string;
  previousPlan: string | null;
  previousSubscriptionStatus: string | null;
  previousTrialEndsAt: string | null;
  newPlan: string;
  newSubscriptionStatus: string;
  newTrialEndsAt: string | null;
}> {
  const payload = await callInternalFunction<{
    data?: {
      companyId: string;
      requestId: string;
      previousPlan: string | null;
      previousSubscriptionStatus: string | null;
      previousTrialEndsAt: string | null;
      newPlan: string;
      newSubscriptionStatus: string;
      newTrialEndsAt: string | null;
    };
    error?: string;
  }>(
    'internal-tenant-plan-override',
    {
      companyId: params.companyId,
      targetPlan: params.targetPlan,
      reason: params.reason,
    }
  );
  if (!payload?.data) {
    throw new Error(payload?.error || 'Unable to override tenant plan.');
  }

  return payload.data;
}

export async function getRecentInternalSupportActions(limit = 10): Promise<InternalSupportActionRow[]> {
  const payload = await callInternalFunction<{
    data?: { rows?: InternalSupportActionRow[] };
    error?: string;
  }>(
    'internal-support-actions-recent',
    { limit }
  );
  if (!payload?.data?.rows) {
    throw new Error(payload?.error || 'Unable to load recent support actions.');
  }

  return payload.data.rows;
}
